// src/components/ui/StatusChip.jsx
import React from 'react';
import { Chip, useTheme, alpha, Avatar } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * A customizable status chip component with support for client/brand status styles
 * @param {Object} props - Component props
 * @param {string} props.label - Text to display in the chip
 * @param {string} props.color - Color for the chip (primary, secondary, success, warning, error, info, default)
 * @param {string} props.variant - Chip variant (filled, outlined)
 * @param {string} props.size - Chip size (small, medium)
 * @param {Object} props.icon - Icon to display at the start of the chip
 * @param {string} props.status - Predefined status (for client, brand, task, etc)
 * @param {string} props.entityType - Type of entity ('client', 'brand', 'task', 'project', etc)
 * @param {string} props.region - Region value (for region chips)
 * @param {Object} props.avatar - Avatar to display in chip
 */
const StatusChip = ({
  label,
  color = 'default',
  variant = 'filled',
  size = 'small',
  icon,
  status,
  entityType,
  region,
  avatar,
  ...rest
}) => {
  const theme = useTheme();
  
  // Client status configuration
  const clientStatusConfig = {
    active: { color: 'success', label: 'Active' },
    inactive: { color: 'error', label: 'Inactive' },
    onboarding: { color: 'warning', label: 'Onboarding' },
    paused: { color: 'info', label: 'Paused' },
    former: { color: 'default', label: 'Former' },
  };

  // Brand status configuration  
  const brandStatusConfig = {
    active: { color: 'success', label: 'Active' },
    inactive: { color: 'error', label: 'Inactive' },
    draft: { color: 'default', label: 'Draft' },
  };

  // Task status configuration
  const taskStatusConfig = {
    to_do: { color: 'default', label: 'To Do' },
    in_progress: { color: 'info', label: 'In Progress' },
    in_review: { color: 'warning', label: 'In Review' },
    done: { color: 'success', label: 'Done' },
    cancelled: { color: 'error', label: 'Cancelled' },
  };

  // Region configuration
  const regionConfig = {
    'Egypt': { color: '#E23D28', icon: '🇪🇬' },
    'Saudi Arabia': { color: '#01401C', icon: '🇸🇦' },
    'Qatar': { color: '#8D1B3D', icon: '🇶🇦' },
    'UAE': { color: '#00732F', icon: '🇦🇪' },
    'Kuwait': { color: '#007A3D', icon: '🇰🇼' },
    'Bahrain': { color: '#CE1126', icon: '🇧🇭' },
    'Oman': { color: '#C8102E', icon: '🇴🇲' },
    'Other': { color: '#666666', icon: '🌍' },
  };

  // Handle status-based chips
  if (status) {
    // Select appropriate config based on entity type
    let config;
    if (entityType === 'client') {
      config = clientStatusConfig[status] || clientStatusConfig.inactive;
    } else if (entityType === 'brand') {
      config = brandStatusConfig[status] || brandStatusConfig.inactive;
    } else if (entityType === 'task') {
      config = taskStatusConfig[status] || taskStatusConfig.to_do;
    } else {
      // Default status handling
      config = {
        label: status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' '),
        color: color
      };
    }

    return (
      <Chip
        label={config.label}
        color={config.color}
        size={size}
        icon={icon || config.icon}
        variant={variant}
        sx={{ fontWeight: 500, ...rest.sx }}
        {...rest}
      />
    );
  }

  // Handle region-based chips
  if (region) {
    const config = regionConfig[region] || regionConfig.Other;
    
    return (
      <Chip 
        label={region} 
        size={size}
        sx={{ 
          backgroundColor: alpha(config.color, 0.1),
          color: config.color,
          fontWeight: 500,
          '& .MuiChip-avatar': {
            color: 'inherit',
            backgroundColor: 'transparent',
          },
          ...rest.sx
        }}
        avatar={<Avatar>{config.icon}</Avatar>}
        {...rest}
      />
    );
  }
  
  // Custom styling for filled variant to make it more subtle
  const getCustomStyling = () => {
    if (variant === 'filled') {
      // Use theme token for status chip styling
      const baseStyles = {
        ...theme.aghrba.tokens.chips.status(theme, color),
        borderColor: 'transparent',
      };
      
      // Ensure proper contrast for primary color
      if (color === 'primary') {
        return {
          ...baseStyles,
          color: theme.palette.primary.contrastText,
          '& .MuiChip-icon': {
            color: theme.palette.primary.contrastText,
          },
          '& .MuiChip-deleteIcon': {
            color: theme.palette.primary.contrastText,
            '&:hover': {
              color: alpha(theme.palette.primary.contrastText, 0.7),
            },
          },
        };
      }
      
      return baseStyles;
    }

    return {};
  };
  
  // Standard label/color chip
  return (
    <Chip
      label={label}
      color={color}
      variant={variant === 'filled' ? 'filled' : 'outlined'}
      size={size}
      icon={icon}
      avatar={avatar}
      sx={{
        ...getCustomStyling(),
        ...(size === 'small' ? theme.aghrba.tokens.chips.sizes.small : {}),
        fontWeight: theme.aghrba.tokens.text.weight.medium,
        ...rest.sx
      }}
      {...rest}
    />
  );
};

StatusChip.propTypes = {
  label: PropTypes.string,
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'warning', 'info', 'default']),
  variant: PropTypes.oneOf(['filled', 'outlined']),
  size: PropTypes.oneOf(['small', 'medium']),
  icon: PropTypes.element,
  status: PropTypes.string,
  entityType: PropTypes.string,
  region: PropTypes.string,
  avatar: PropTypes.element
};

export default StatusChip;